/* eslint-disable jsx-a11y/alt-text */
import * as React from "react";
import moment from "moment";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { TxtInput } from "../../../components";
import { useQuery } from "@apollo/client";
import { GET_WAIT_LIST } from "../../../apolloClient/operation/querys";
import AppLoader from "../../../components/loader";
import { LeadsData } from "../../../types/graphQlQuery.interface";
import { LEAD_DRAWER_DETAILS } from "../../../contants/projectDetails";
import { LeadDrawerDetails } from "../../../types/type";
import DisplayWaitListDetailsCard from "../Leads/DisplayLeadDetailCard";
import searchIcon from "../../../images/searchIcon.png";
import cancelIcon from "../../../images/CancelIcon.png";

import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox,
  SearchIcon,
  TableWrapper,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  PrimaryCell,
  SecondaryText,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";

interface WaitlistTableProps {
  waitList: LeadsData[];
}

export const WaitlistTable: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);
  const [keyword, setKeyword] = React.useState("");
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [selectedWaitList, setSelectedWaitList] = React.useState<LeadsData | null>(null);

  const {
    loading,
    error,
    data: waitlistData,
  } = useQuery<WaitlistTableProps>(GET_WAIT_LIST, {
    variables: { limit: 1000, offset: 0 },
  });

  const filterWaitlist = () => {
    if (!waitlistData?.waitList) return [];
    if (keyword.length > 0) {
      return waitlistData.waitList.filter((item) => {
        const city = item.city?.toLowerCase() || "";
        const state = item.state?.toLowerCase() || "";
        const address = `${city} ${state}`;

        return (
          city.includes(keyword.toLowerCase()) ||
          state.includes(keyword.toLowerCase()) ||
          address.includes(keyword.toLowerCase())
        );
      });
    }
    return waitlistData.waitList;
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    const totalPages = Math.ceil(filterWaitlist().length / rowsPerPage);
    setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const handleDrawerOpen = (waitlistItem: LeadsData) => {
    return () => {
      setSelectedWaitList(waitlistItem);
      setOpenDrawer(true);
    };
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
  };

  if (loading) {
    return <AppLoader isLoading={true} />;
  }

  const filteredWaitlist = filterWaitlist();
  const paginatedWaitlist = [...filteredWaitlist]
    .reverse()
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const totalPages = Math.ceil(filteredWaitlist.length / rowsPerPage);

  const showWaitListDetails = () => {
    return (
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            height: "100%",
            width: isMobile ? "100%" : "420px",
            borderTopLeftRadius: isMobile ? 0 : "16px",
            borderBottomLeftRadius: isMobile ? 0 : "16px",
            overflowX: "hidden",
          },
        }}
      >
        <Box sx={{ p: 3, height: "100%", backgroundColor: "#fff" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              onClick={handleDrawerClose}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "#f3f4f6",
                transition: "background-color 0.2s",
                "&:hover": { backgroundColor: "#e5e7eb" },
              }}
            >
              <img src={cancelIcon} alt="Close" style={{ width: 12, height: 12 }} />
            </Box>
            <Box>
              <Box sx={{ fontSize: 18, fontWeight: 600, color: "#1a1a2e" }}>
                Waitlist #{selectedWaitList?.id}
              </Box>
              <Box sx={{ fontSize: 13, color: "#6b7280" }}>
                {selectedWaitList?.city}, {selectedWaitList?.state}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {LEAD_DRAWER_DETAILS.map((detail: LeadDrawerDetails, index: number) => (
              <DisplayWaitListDetailsCard
                key={`waitlist-detail-${index}`}
                label={detail.label}
                value={
                  detail.isDate
                    ? selectedWaitList?.[detail.key as keyof LeadsData]
                      ? new Date(selectedWaitList?.[detail.key as keyof LeadsData] as string).toLocaleDateString()
                      : ""
                    : (selectedWaitList?.[detail.key as keyof LeadsData] as string) || ""
                }
              />
            ))}
          </Box>
        </Box>
      </Drawer>
    );
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <TitleRow>
            <PageTitle>Waitlist</PageTitle>
          </TitleRow>
          <PageSubtitle>{filteredWaitlist.length} total entries</PageSubtitle>
        </HeaderSection>

        <Box sx={{ mb: 3 }}>
          <SearchInputBox>
            <SearchIcon
              src={searchIcon}
              style={{ margin: "0 12px" }}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search by city or state..."
              onChange={setKeyword}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInputBox>
        </Box>

        <TableWrapper>
          <TableHeader>
            <TableHeaderCell width="35%">City</TableHeaderCell>
            <TableHeaderCell width="35%">State</TableHeaderCell>
            <TableHeaderCell width="20%">Created</TableHeaderCell>
            <TableHeaderCell width="10%"></TableHeaderCell>
          </TableHeader>

          {filteredWaitlist.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No waitlist entries found</EmptyStateText>
            </EmptyState>
          ) : (
            paginatedWaitlist.map((item, index) => (
              <TableRow key={`waitlist-${item.id}-${index}`} onClick={handleDrawerOpen(item)}>
                <PrimaryCell width="35%" mobileLabel="City">
                  {item.city || "-"}
                </PrimaryCell>
                <TableCell width="35%" mobileLabel="State">
                  {item.state || "-"}
                </TableCell>
                <TableCell width="20%" mobileLabel="Created">
                  <SecondaryText>{moment(item.createdAt).fromNow(true)} ago</SecondaryText>
                </TableCell>
                <TableCell width="10%" style={{ textAlign: "right" }}>
                  <Box
                    component="span"
                    sx={{
                      color: "#4b87ed",
                      fontSize: 13,
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    View →
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}

          {filteredWaitlist.length > 0 && totalPages > 1 && (
            <PaginationWrapper>
              <Box
                component="button"
                onClick={goToPreviousPage}
                disabled={page === 0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                  color: page === 0 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page === 0 ? "not-allowed" : "pointer",
                  "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                ← Prev
              </Box>
              <PaginationText>
                Page {page + 1} of {totalPages}
              </PaginationText>
              <Box
                component="button"
                onClick={goToNextPage}
                disabled={page >= totalPages - 1}
                sx={{
                  px: 2,
                  py: 1,
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                  color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                  "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
                }}
              >
                Next →
              </Box>
            </PaginationWrapper>
          )}
        </TableWrapper>

        {showWaitListDetails()}
      </ContentWrapper>
    </PageContainer>
  );
};
